import { Component, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TableModule } from 'primeng/table';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { ToastrService } from 'ngx-toastr';
import {
  ReconciliationService,
  type ReconciliationDTO,
  type ReconciliationFilterSpec,
  type ReconciliationLineDTO
} from '../../services/reconciliation.service';
import { ChargeService } from '../../services/charge.service';
import { SaleService, type SaleDTO } from '../../services/sale.service';
import { CustomerService, type CustomerDTO } from '../../services/customer.service';
import { ChargeReadComponent } from '../charges/charge-read.component';
import { SaleReadComponent } from '../sales/sale-read.component';
import { CustomerReadComponent } from '../customers/customer-read.component';
import type { ChargeDTO } from '../../core/models';
import { sumReconciliationLineAmounts, type ReconciliationLineTotals } from './reconciliation-line-totals';
import {
  reconciliationChargeLabel,
  reconciliationCustomerLabel,
  reconciliationSaleLabel
} from './reconciliation-linked-entities';
import type { Pageable } from '../../services/service-common';

function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Rango de los últimos 30 días (incluye hoy y 29 días hacia atrás). */
function defaultLast30DaysRange(): [Date, Date] {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return [start, end];
}

@Component({
  selector: 'app-reconciliations-list',
  standalone: true,
  templateUrl: './reconciliations-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host ::ng-deep .reconciliations-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      :host ::ng-deep .p-paginator .p-paginator-page.p-paginator-page-selected,
      :host ::ng-deep button.p-paginator-page-selected {
        background: rgb(224 242 254) !important;
        border-color: rgb(14 165 233) !important;
        color: rgb(2 132 199) !important;
      }
      :host ::ng-deep .reconciliation-drawer-lines-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.65rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        white-space: nowrap;
      }
      :host ::ng-deep .reconciliation-drawer-lines-table .p-datatable-tbody > tr > td {
        font-size: 0.75rem;
        vertical-align: top;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-header,
      :host ::ng-deep .customer-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-content,
      :host ::ng-deep .customer-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    RouterLink,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatTooltipModule,
    TableModule,
    DrawerModule,
    FloatLabelModule,
    DatePickerModule,
    ButtonModule,
    SkeletonModule,
    TooltipModule,
    ChargeReadComponent,
    SaleReadComponent,
    CustomerReadComponent
  ]
})
export class ReconciliationsListComponent {
  private reconciliationService = inject(ReconciliationService);
  private chargeService = inject(ChargeService);
  private saleService = inject(SaleService);
  private customerService = inject(CustomerService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  items = signal<ReconciliationDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  drawerReconciliation = signal<ReconciliationDTO | null>(null);
  drawerLoading = signal(false);
  isDrawerOpen = signal(false);

  selectedCharge = signal<ChargeDTO | null>(null);
  isChargeDetailsOpen = signal(false);
  chargeDetailsLoading = signal(false);

  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  isSaleDetailsOpen = signal(false);
  saleDetailsLoading = signal(false);

  selectedCustomer = signal<CustomerDTO | null>(null);
  isCustomerDetailsOpen = signal(false);
  customerDetailsLoading = signal(false);

  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(0);
  pageSizeOptions = [20, 50, 100];
  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];

  filtersForm = this.fb.group({
    businessDateRange: [null as [Date, Date] | null]
  });

  constructor() {
    this.filtersForm.patchValue({ businessDateRange: defaultLast30DaysRange() });
    this.loadList();
  }

  loadList(): void {
    this.loading.set(true);
    this.error.set(null);
    const spec = this.buildSpec();
    const pageable: Pageable = { page: this.currentPage(), size: this.pageSize(), sort: ['businessDate,desc'] };
    this.reconciliationService.search(spec, pageable).subscribe({
      next: response => {
        if (response.ok) {
          this.items.set(response.body ?? []);
          if (response.page) {
            this.totalItems.set(response.page.totalItems);
            this.totalPages.set(response.page.totalPages);
            this.pageSize.set(response.page.pageSize);
            this.currentPage.set(Math.max(0, response.page.currentPage - 1));
          }
        } else {
          this.error.set(response.message || 'Error al cargar liquidaciones');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: err => {
        console.error(err);
        this.error.set('No se pudieron cargar las liquidaciones.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
      }
    });
  }

  private buildSpec(): ReconciliationFilterSpec {
    const spec: ReconciliationFilterSpec = {};
    const range = this.filtersForm.get('businessDateRange')?.value;
    if (range && range[0] && range[1]) {
      spec['businessDate[after]'] = toLocalYmd(range[0]);
      spec['businessDate[before]'] = toLocalYmd(range[1]);
    }
    return spec;
  }

  onLazyLoad(event: { first: number; rows: number }): void {
    const page = event.rows ? Math.floor(event.first / event.rows) : 0;
    const size = event.rows || this.pageSize();
    if (page === this.currentPage() && size === this.pageSize()) return;
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.loadList();
  }

  applyFilters(): void {
    this.currentPage.set(0);
    this.loadList();
  }

  clearFilters(): void {
    this.filtersForm.patchValue({ businessDateRange: defaultLast30DaysRange() });
    this.applyFilters();
  }

  openDrawer(row: ReconciliationDTO): void {
    if (!row.id) return;
    this.isDrawerOpen.set(true);
    this.drawerLoading.set(true);
    this.drawerReconciliation.set(null);
    this.reconciliationService.getById(row.id).subscribe({
      next: res => {
        if (res.ok && res.body) {
          this.drawerReconciliation.set(res.body);
        }
        this.drawerLoading.set(false);
      },
      error: () => {
        this.drawerLoading.set(false);
      }
    });
  }

  onDrawerVisibleChange(visible: boolean): void {
    this.isDrawerOpen.set(visible);
    if (!visible) {
      this.drawerReconciliation.set(null);
    }
  }

  goToDetail(id: number): void {
    this.router.navigate(['/reconciliations', id]);
  }

  lineTotals(lines: ReconciliationLineDTO[] | undefined | null): ReconciliationLineTotals {
    return sumReconciliationLineAmounts(lines);
  }

  saleLabel(line: ReconciliationLineDTO): string {
    return reconciliationSaleLabel(line);
  }

  chargeLabel(line: ReconciliationLineDTO): string {
    return reconciliationChargeLabel(line);
  }

  customerLabel(line: ReconciliationLineDTO): string {
    return reconciliationCustomerLabel(line);
  }

  openChargeDetails(charge: ChargeDTO | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!charge?.id || this.chargeDetailsLoading()) return;
    this.chargeDetailsLoading.set(true);
    this.chargeService.searchCharges({ id: charge.id }, { page: 0, size: 1 }).subscribe({
      next: response => {
        this.chargeDetailsLoading.set(false);
        this.selectedCharge.set(response.body?.[0] ?? charge);
        this.isChargeDetailsOpen.set(true);
      },
      error: () => {
        this.chargeDetailsLoading.set(false);
        this.selectedCharge.set(charge);
        this.isChargeDetailsOpen.set(true);
      }
    });
  }

  onChargeDrawerVisibleChange(visible: boolean): void {
    this.isChargeDetailsOpen.set(visible);
    if (!visible) this.selectedCharge.set(null);
  }

  openSaleDetails(saleId: number | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!saleId) return;
    this.isSaleDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: response => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          this.saleService.getSaleCharges(detail.id!).subscribe({
            next: chargesResp => this.saleCharges.set(chargesResp.body ?? []),
            error: () => this.saleCharges.set([])
          });
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle de la venta');
          this.closeSaleDetails();
        }
        this.saleDetailsLoading.set(false);
      },
      error: () => {
        this.saleDetailsLoading.set(false);
        this.toastr.error('Error al cargar el detalle de la venta');
        this.closeSaleDetails();
      }
    });
  }

  closeSaleDetails(): void {
    this.isSaleDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleDetails();
  }

  openSaleDetailsFromCustomer(saleId: number): void {
    this.openSaleDetails(saleId);
  }

  openCustomerDetails(customerId: number | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: response => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: () => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('Error al cargar el cliente');
        this.closeCustomerDetails();
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }

  /** Muestra solo la parte de fecha (sin hora) para valores ISO o con espacio. */
  transactionDateOnly(raw?: string | null): string {
    if (raw == null || String(raw).trim() === '') {
      return '—';
    }
    const s = String(raw).trim();
    if (s.includes('T')) {
      return s.split('T')[0] ?? s;
    }
    const space = s.indexOf(' ');
    if (space > 0) {
      return s.substring(0, space);
    }
    return s;
  }
}
